'use client'

import { useState, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import posthog from 'posthog-js'
import { useTaxCalculator } from '@/modules/hs/hooks/useHsTax'

import { TaxCalculation } from '@/modules/hs/types/tax.types'
import { useAuth } from '@/modules/auth/contexts/AuthContext'
import Link from '@/modules/shared/components/Link'
import { HsCodeDto } from '@/modules/hs/types/hs-response'
import CountrySelector from '../../common/CountrySelector'
import {
  Calculator,
  Zap,
  SlidersHorizontal,
  CircleHelp,
  Circle,
  Check,
  X,
  Info,
  List,
  Pencil,
  DollarSign,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import TaxCalculatorInput from '../component/TaxCalculatorInput'
import { NT_KHONG_TACH_TNDN, NT_KHONG_TACH_VAT, SERVICE_TAX_RATES } from '@/modules/hs/constants/service-tax-rate.constant'
import { countries } from '@/modules/hs/constants/country.constant'
import NKUDDB_FORM from '@/modules/hs/constants/nkuddb-form.json'
import { useServiceTax } from '@/modules/hs/hooks/useServiceTax'
import HsTaxCalculatorResultCard from './component/HsTaxCalculatorResultCard'
import { TaxNote } from '@/modules/hs/types/tax.types'
import { getTtdbNote, isTaxNoteCode } from '@/modules/hs/utils/hstax-note.util'

interface HsTaxCalculatorV2Props {
  hsCode: string
  hs: HsCodeDto
  notes: TaxNote[]
  vatRate: number           // đã clamp >= 0, dùng trong simple mode
  vatRawRate?: number       // giá trị gốc từ DB (có thể âm = note code)
  vatNote?: string          // text ghi chú khi VAT là note code
  nkuddb?: Record<string, any[]>  // FTA data from taxList
  excludeCountries?: Record<string, (number | string)[]>  // excluded countries per taxId
  taxByCountries?: Record<number, Array<{ countryId: number; countryName: string }>>  // countries per taxId
}


export default function HsTaxCalculatorV2({ hsCode, hs, notes, vatRate, vatRawRate, vatNote, nkuddb, excludeCountries, taxByCountries }: HsTaxCalculatorV2Props) {

  // Mode toggle
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple')
  const [showHelpText, setShowHelpText] = useState(false)

  // Tỷ giá - có thể chỉnh sửa bởi người dùng
  const [exchangeRate, setExchangeRate] = useState(26000)
  const [isEditingRate, setIsEditingRate] = useState(false)

  // Shared states between modes
  const [quantity, setQuantity] = useState(50000)
  const [country, setCountry] = useState('China')
  const [currency, setCurrency] = useState<'USD' | 'VND'>('USD')
  const [selectedFTA, setSelectedFTA] = useState<string>('no-co')  // 'no-co' = no C/O, 'no-co-fta' = no C/O but FTA eligible, 'ACFTA', 'CPTPP', etc.
  const [unitPrice, setUnitPrice] = useState(10)

  // Advanced mode specific states
  const [importDate, setImportDate] = useState(new Date().toISOString().split('T')[0])
  const [incotermType, setIncotermType] = useState<'FOB' | 'CIF'>('FOB')
  const [freight, setFreight] = useState(0)
  const [insurance, setInsurance] = useState(0)
  const [ttdbRate, setTtdbRate] = useState<number | undefined>(undefined)
  // VAT input state cho advanced mode: undefined nếu là note code (cần nhập tay)
  const [vatRateInput, setVatRateInput] = useState<number | undefined>(
    vatRawRate !== undefined && vatRawRate < 0 ? undefined : vatRate
  )

  // Service management using custom hook
  const {
    hasService,
    setHasService,
    serviceSeparated,
    setServiceSeparated,
    services,
    addService,
    removeService,
    updateServiceType,
    updateServiceValue,
  } = useServiceTax()

  // Results
  const [taxCalculationLines, setTaxCalculationLines] = useState<TaxCalculation[]>([])
  const [calculatedGoodsValue, setCalculatedGoodsValue] = useState(0)

  // Handler to switch mode and clear results
  const handleModeChange = (newMode: 'simple' | 'advanced') => {
    setMode(newMode)
    setTaxCalculationLines([])
    setCalculatedGoodsValue(0)
  }

  // Current active country (based on mode)
  const activeCountry = country

  const { data: taxCalculator } = useTaxCalculator(hsCode, activeCountry, importDate)

  // Look up TTDB note: khi taxTTDB âm thì là mã ghi chú
  const ttdbNote = getTtdbNote(taxCalculator?.taxTTDB, notes)

  // Nếu taxNKUD < 0 thì là note code — chưa đủ điều kiện để tính thuế bình thường
  const isNkudNote = isTaxNoteCode(taxCalculator?.taxNKUD)

  // Filter available FTA forms based on selected country and excludeCountries
  const availableFTAOptions = useMemo(() => {
    if (!nkuddb || !taxByCountries) return []

    // Get countryId from selected country name
    const selectedCountryData = countries.find(c => c.value === country)
    const selectedCountryId = selectedCountryData?.id

    const options: Array<{ key: string; form: string; agreement: string; taxRate: number }> = []

    Object.entries(nkuddb).forEach(([ftaKey, taxes]) => {
      if (!taxes || taxes.length === 0) return

      // Get current effective tax
      const currentDate = new Date()
      const currentTax = taxes.find((tax) => {
        const fromDate = new Date(tax.effectiveFrom)
        const toDate = new Date(tax.effectiveTo)
        return currentDate >= fromDate && currentDate <= toDate
      })

      if (!currentTax || currentTax.taxRate < 0) return

      // Get applied countries from taxByCountries
      const allCountries = taxByCountries[Number(currentTax.taxId)] || []
      const appliedCountryIds = allCountries.map((c) => c.countryId)
      const isCountryApplied = selectedCountryId && appliedCountryIds.includes(selectedCountryId)

      // Skip this FTA if the selected country is NOT in the applied countries list
      if (!isCountryApplied) {
        return
      }

      // Check if this FTA is excluded for the selected country
      const taxIdStr = String(currentTax.taxId)
      const excludedIds = excludeCountries?.[taxIdStr] || excludeCountries?.[Number(currentTax.taxId)] || []
      const excludedSet = new Set(excludedIds.map((id: number | string) => typeof id === 'string' ? parseInt(id, 10) : id))

      // Skip this FTA if the selected country is excluded
      if (excludedSet.has(selectedCountryId)) {
        return
      }

      // Get form info from NKUDDB_FORM
      const formInfo = NKUDDB_FORM.find((f) => f.key === ftaKey)

      options.push({
        key: ftaKey,
        form: formInfo?.form || ftaKey,
        agreement: formInfo?.key || ftaKey,
        taxRate: currentTax.taxRate,
      })
    })

    return options.sort((a, b) => a.taxRate - b.taxRate)
  }, [nkuddb, taxByCountries, excludeCountries, country])

  // Get tax rates for display in dropdown
  const nkttRate = taxCalculator?.taxNKTT ?? 0
  const nkudRate = taxCalculator?.taxNKUD ?? nkttRate  // Fallback to NKTT if no NKUD


  // Reset selected FTA when country changes and current selection is no longer available
  useEffect(() => {
    // Only reset if a specific FTA is selected (not 'no-co' or 'no-co-fta')
    if (selectedFTA !== 'no-co' && selectedFTA !== 'no-co-fta' && availableFTAOptions.length > 0) {
      const isStillAvailable = availableFTAOptions.some(fta => fta.key === selectedFTA)
      if (!isStillAvailable) {
        setSelectedFTA('no-co')
      }
    }
  }, [country, availableFTAOptions, selectedFTA])

  useEffect(() => {
    if (taxCalculator) {
      setTtdbRate(taxCalculator.taxTTDB)
    }
  }, [taxCalculator])

  // Auto recalculate when selected FTA changes (if result is already shown)
  useEffect(() => {
    if (taxCalculator) {
      calculateTax()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFTA])



  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value)
  }



  // Service functions for advanced mode

  const calculateTax = () => {
    if (!taxCalculator) return

    const calcs: TaxCalculation[] = []
    const effectiveExchangeRate = currency === 'VND' ? 1 : exchangeRate

    // Calculate goods value based on mode
    let goodsValue = 0
    if (mode === 'simple') {
      // Simple: unitPrice * quantity * effectiveExchangeRate
      goodsValue = quantity * unitPrice * effectiveExchangeRate
    } else {
      // Advanced: consider incoterm, freight, insurance
      if (incotermType === 'FOB') {
        goodsValue = (quantity * unitPrice + freight + insurance) * effectiveExchangeRate
      } else {
        goodsValue = quantity * unitPrice * effectiveExchangeRate
      }
    }
    setCalculatedGoodsValue(goodsValue)

    // 1. Thuế NK (Import Tax)
    let taxNKRate = 0
    let taxNKName = 'xxx'

    const hasNoCOFTA = selectedFTA === 'no-co-fta'  // Không có C/O nhưng thỏa mãn ưu đãi FTA → dùng NKUD
    const hasCO = selectedFTA !== 'no-co' && selectedFTA !== 'no-co-fta'  // Có C/O FTA cụ thể

    // Get FTA info for display - use the specific tax rate from selected FTA
    const selectedFTAInfo = availableFTAOptions.find(f => f.key === selectedFTA)

    if (hasNoCOFTA) {
      // Không có C/O nhưng thỏa mãn ưu đãi FTA: dùng thuế NK ưu đãi (NKUD) theo country
      taxNKRate = taxCalculator.taxNKUD ?? taxCalculator.taxNKTT ?? 0
      taxNKName = 'Thuế nhập khẩu (Ưu đãi - MFN)'
    } else if (hasCO && selectedFTAInfo) {
      // Use the tax rate from the selected FTA
      taxNKRate = selectedFTAInfo.taxRate
      taxNKName = `Thuế nhập khẩu (${selectedFTAInfo.form} - ${selectedFTAInfo.agreement})`
    } else if (hasCO && taxCalculator.taxNKUDDB !== undefined && taxCalculator.taxNKUDDB !== null && taxCalculator.taxNKUDDB >= 0) {
      taxNKRate = taxCalculator.taxNKUDDB
      taxNKName = 'Thuế nhập khẩu (Ưu đãi đặc biệt - FTA)'
    } else if (hasCO && taxCalculator.taxNKUD !== undefined && taxCalculator.taxNKUD !== null) {
      taxNKRate = taxCalculator.taxNKUD
      taxNKName = 'Thuế nhập khẩu (Ưu đãi - MFN)'
    } else if (taxCalculator.taxNKTT !== undefined && taxCalculator.taxNKTT !== null) {
      taxNKRate = taxCalculator.taxNKTT
      taxNKName = 'Thuế nhập khẩu (Thuế suất thông thường)'
    }

    if (taxNKRate < 0) {
      if (mode === 'advanced') {
        taxNKName = `Thuế nhập khẩu (${taxNKRate})`
      }
      taxNKRate = 0
    }

    const taxNK = goodsValue * (taxNKRate / 100)
    const valueAfterNK = goodsValue + taxNK

    calcs.push({
      taxType: 'tuongdoi',
      taxName: taxNKName,
      taxAmount: taxNK,
      baseAmount: goodsValue,
      finalAmount: valueAfterNK,
      taxRate: taxNKRate,
    })

    // 2. Thuế TTDB (Special Consumption Tax)
    // Backend ensures taxTTDB >= 0; negative values (note codes) are clamped to 0 at source
    const rawTtdbRate = mode === 'simple' ? (taxCalculator.taxTTDB ?? 0) : (ttdbRate ?? 0)
    const ttdbRateNumber = Math.max(0, rawTtdbRate)
    const taxTTDB = valueAfterNK * (ttdbRateNumber / 100)
    const valueAfterTTDB = valueAfterNK + taxTTDB

    if (ttdbRateNumber > 0) {
      calcs.push({
        taxType: 'tuongdoi',
        taxName: mode === 'simple' ? 'Thuế tiêu thụ đặc biệt' : 'Thuế tiêu thụ đặc biệt (TTĐB)',
        taxAmount: taxTTDB,
        baseAmount: valueAfterNK,
        finalAmount: valueAfterTTDB,
        taxRate: ttdbRateNumber,
      })
    }

    // 3. Thuế BVMT (Environmental Protection Tax)
    const taxBVMT = (taxCalculator.taxBVMT || 0) * quantity

    if (taxCalculator.taxBVMT && taxCalculator.taxBVMT > 0) {
      calcs.push({
        taxType: 'tuyetdoi',
        taxName: 'Thuế BVMT',
        taxAmount: taxBVMT,
        baseAmount: quantity,
        finalAmount: taxBVMT,
        taxRate: taxCalculator.taxBVMT,
      })
    }

    // 4. Thuế VAT (Value Added Tax)
    // simple: dùng vatRate từ prop (đã clamp); advanced: dùng input của user
    const effectiveVatRate = mode === 'simple' ? vatRate : Math.max(0, vatRateInput ?? vatRate)
    const baseForVAT = valueAfterTTDB + taxBVMT
    const taxVAT = baseForVAT * (effectiveVatRate / 100)

    calcs.push({
      taxType: 'tuongdoi',
      taxName: mode === 'simple' ? 'Thuế GTGT (VAT)' : 'Thuế giá trị gia tăng (GTGT)',
      taxAmount: taxVAT,
      baseAmount: baseForVAT,
      finalAmount: baseForVAT + taxVAT,
      taxRate: effectiveVatRate,
    })

    // 5. Thuế nhà thầu (Contractor Tax) - Only in advanced mode
    if (mode === 'advanced' && hasService) {
      if (serviceSeparated === 'no') {
        const taxNT_VAT = goodsValue * (NT_KHONG_TACH_VAT / 100)
        const taxNT_TNDN = goodsValue * (NT_KHONG_TACH_TNDN / 100)

        calcs.push({
          taxType: 'tuongdoi',
          taxName: 'Thuế nhà thầu (VAT)',
          taxAmount: taxNT_VAT,
          baseAmount: goodsValue,
          finalAmount: goodsValue + taxNT_VAT,
          taxRate: NT_KHONG_TACH_VAT,
        })

        calcs.push({
          taxType: 'tuongdoi',
          taxName: 'Thuế nhà thầu (TNDN)',
          taxAmount: taxNT_TNDN,
          baseAmount: goodsValue,
          finalAmount: goodsValue + taxNT_TNDN,
          taxRate: NT_KHONG_TACH_TNDN,
        })
      } else {
        const vatSubItems: Array<{ name: string; taxAmount: number; baseAmount: number; taxRate: number }> = []
        const tndnSubItems: Array<{ name: string; taxAmount: number; baseAmount: number; taxRate: number }> = []
        let totalVAT = 0
        let totalTNDN = 0

        services.forEach((service, index) => {
          if (service.type !== '-1' && service.value > 0) {
            const taxRates = SERVICE_TAX_RATES[service.type]
            if (taxRates) {
              const serviceValueVND = service.value * exchangeRate
              const letters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']

              const taxNT_VAT = serviceValueVND * (taxRates.vat / 100)
              totalVAT += taxNT_VAT
              vatSubItems.push({
                name: `${letters[index]}) ${taxRates.name}`,
                taxAmount: taxNT_VAT,
                baseAmount: serviceValueVND,
                taxRate: taxRates.vat,
              })

              const taxNT_TNDN = serviceValueVND * (taxRates.tndn / 100)
              totalTNDN += taxNT_TNDN
              tndnSubItems.push({
                name: `${letters[index]}) ${taxRates.name}`,
                taxAmount: taxNT_TNDN,
                baseAmount: serviceValueVND,
                taxRate: taxRates.tndn,
              })
            }
          }
        })

        if (vatSubItems.length > 0) {
          calcs.push({
            taxType: 'tuongdoi',
            taxName: 'Thuế nhà thầu (VAT)',
            taxAmount: totalVAT,
            baseAmount: 0,
            finalAmount: totalVAT,
            taxRate: 0,
            subItems: vatSubItems,
          })
        }

        if (tndnSubItems.length > 0) {
          calcs.push({
            taxType: 'tuongdoi',
            taxName: 'Thuế nhà thầu (TNDN)',
            taxAmount: totalTNDN,
            baseAmount: 0,
            finalAmount: totalTNDN,
            taxRate: 0,
            subItems: tndnSubItems,
          })
        }
      }
    }

    setTaxCalculationLines(calcs)

    posthog.capture('hs_code_tax_calculated', {
      hs_id: hsCode,
      context: mode,
      don_gia: unitPrice,
      khoi_luong: quantity,
      nuoc_xuat_khau: country,
      currency,
      import_date: importDate,
      incoterm: mode === 'advanced' ? incotermType : undefined,
    })
  }

  // Calculate goods value formula string
  const goodsValueFormula = (() => {
    if (mode === 'simple') {
      if (currency === 'VND') {
        return `${formatNumber(unitPrice)} VNĐ x ${formatNumber(quantity)}`
      }
      return `${formatNumber(unitPrice)} ${currency} x ${formatNumber(quantity)} x ${formatNumber(exchangeRate)}`
    } else {
      if (currency === 'VND') {
        return `${formatNumber(unitPrice)} VNĐ x ${formatNumber(quantity)}`
      }
      return `${formatNumber(unitPrice)} ${currency} x ${formatNumber(quantity)} x ${formatNumber(exchangeRate)}`
    }
  })()



  return (
    <div className="cl-card-border mt-6" id="tax-calculator">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="text-[#1E40AF] h-5 w-5" />
          <h2 className="text-xl font-bold">
            Tính thuế nhập khẩu
          </h2>
        </div>
        <p className="text-lg text-gray-500">Mã hàng: {hsCode}</p>
      </div>

      {/* Mode Toggle */}
      <div className="flex rounded-lg bg-gray-100 p-2 mb-4">
        <button
          onClick={() => handleModeChange('simple')}
          className={clsx(
            'flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all hover:bg-(--primary-blue-light) cursor-pointer',
            mode === 'simple' ? 'bg-white text-[#1E40AF] shadow-sm' : 'text-gray-600 hover:text-gray-900'
          )}
        >
          <Zap className="h-3.5 w-3.5" />
          Tính nhanh
        </button>
        <button
          onClick={() => handleModeChange('advanced')}
          className={clsx(
            'flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all hover:bg-(--primary-blue-light) cursor-pointer',
            mode === 'advanced' ? 'bg-white text-[#1E40AF] shadow-sm' : 'text-gray-600 hover:text-gray-900'
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Tính chuyên sâu
        </button>
      </div>

      {/* Help Text Toggle */}
      <label className="flex items-center gap-2 mb-6 cursor-pointer text-sm text-gray-600">
        <input
          type="checkbox"
          checked={showHelpText}
          onChange={(e) => setShowHelpText(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
        />
        <span className="flex items-center gap-1.5">
          <CircleHelp className="h-4 w-4 text-gray-400" />
          Hiển thị hướng dẫn chi tiết
        </span>
      </label>

      {/* Simple Form */}
      {mode === 'simple' && (
        <div className="space-y-5">
          {/* Tax Types Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-3">Công cụ tính được các loại thuế sau (nếu có áp dụng):</div>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Circle className="h-1 w-1 fill-[#1E40AF] text-[#1E40AF]" />
                Thuế nhập khẩu
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Circle className="h-1 w-1 fill-[#1E40AF] text-[#1E40AF]" />
                Thuế VAT
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Circle className="h-1 w-1 fill-[#1E40AF] text-[#1E40AF]" />
                Thuế nhà thầu
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Circle className="h-1 w-1 fill-[#1E40AF] text-[#1E40AF]" />
                Thuế bảo vệ môi trường
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Circle className="h-1 w-1 fill-[#1E40AF] text-[#1E40AF]" />
                Thuế tiêu thụ đặc biệt
              </div>

            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Đơn giá + Tỷ giá - cùng 1 grid cell */}
            <div>
              <div className="flex gap-2 items-start">
                <TaxCalculatorInput
                  label="Đơn giá (FOB)"
                  value={unitPrice}
                  onChange={setUnitPrice}
                  placeholder="Nhập đơn giá..."
                  helperText={`Giá FOB (Free on Board) mỗi ${hs.donviVN || 'kg'} - Giá chưa bao gồm vận chuyển và bảo hiểm`}
                  helperIcon="$"
                  showHelperText={showHelpText}
                  className="flex-1"
                />
                {/* Spacer vô hình = chiều cao label (text-sm + mb-1.5) để Select căn ngang với input */}
                <div className="flex flex-col">
                  <span className="block text-sm mb-1.5 opacity-0 select-none" aria-hidden>·</span>
                  <Select value={currency} onValueChange={(value) => setCurrency(value as 'USD' | 'VND')}>
                    <SelectTrigger className="w-24" size='md'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="VND">VND</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tỷ giá - hiện khi không phải VND */}
              {currency !== 'VND' && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-2">
                  <DollarSign className="h-3 w-3 shrink-0 text-gray-400" />
                  <span className="text-gray-400">Tỷ giá:</span>
                  {isEditingRate ? (
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">1 {currency} =</span>
                      <input
                        type="number"
                        value={exchangeRate}
                        onChange={(e) => setExchangeRate(Number(e.target.value) || 1)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') setIsEditingRate(false)
                        }}
                        className="w-24 border border-blue-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <span className="text-gray-500">VND</span>
                      <button
                        type="button"
                        onClick={() => setIsEditingRate(false)}
                        className="text-green-600 hover:text-green-700"
                        title="Xác nhận"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span>1 {currency} = <strong>{exchangeRate.toLocaleString('vi-VN')}</strong> VND</span>
                      <button
                        type="button"
                        onClick={() => setIsEditingRate(true)}
                        className="text-gray-300 hover:text-[#1E40AF] ml-0.5 transition-colors"
                        title="Chỉnh tỷ giá"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Khối lượng */}
            <div className="flex gap-2 items-start">
              <TaxCalculatorInput
                label="Khối lượng hàng hóa"
                value={quantity}
                onChange={setQuantity}
                placeholder="Nhập khối lượng..."
                helperText="Khối lượng hàng hóa (dùng để tính giá trị hàng và thuế BVMT)"
                helperIcon="⚖"
                showHelperText={showHelpText}
                className="flex-1"
              />
              {/* Spacer vô hình = chiều cao label */}
              <div className="flex flex-col">
                <span className="block text-sm mb-1.5 opacity-0 select-none" aria-hidden>·</span>
                <div className="flex items-center justify-center px-3 py-[9px] text-sm border border-gray-200 rounded-md text-gray-600 min-w-[60px]">
                  {hs.donviVN || 'Chưa xác định'}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nước xuất khẩu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nước xuất khẩu</label>
              <CountrySelector
                value={country}
                onValueChange={setCountry}
              />
              {showHelpText && (
                <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                  <span className="text-gray-400 mt-0.5">🌍</span>
                  Chọn nước xuất khẩu để áp dụng thuế suất FTA phù hợp
                </p>
              )}
            </div>

            {/* Form C/O Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Form C/O (Giấy chứng nhận xuất xứ)
              </label>
              <Select value={selectedFTA} onValueChange={setSelectedFTA}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Chọn Form C/O" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-co">
                    <span >Không có C/O: {nkttRate}%</span>
                  </SelectItem>
                  <SelectItem value="no-co-fta">
                    <span >Không có C/O theo các hiệp định FTA: {nkudRate}%</span>
                  </SelectItem>
                  {availableFTAOptions.map((fta) => (
                    <SelectItem key={fta.key} value={fta.key}>
                      <span className="font-medium">{fta.form} - {fta.agreement}: {fta.taxRate}%</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showHelpText && (
                <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                  <span className="text-gray-400 mt-0.5">📜</span>
                  {availableFTAOptions.length > 0
                    ? `Có ${availableFTAOptions.length} FTA khả dụng cho nước này. Chọn Form C/O để được hưởng ưu đãi thuế.`
                    : 'Không có FTA nào khả dụng cho nước này. Sẽ áp dụng thuế MFN hoặc thuế NK thông thường.'}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="groupp relative">
            <Button
              onClick={calculateTax}
              disabled={isNkudNote}
              className="w-full bg-[#1E40AF] hover:bg-[#1a3a9e] text-white py-3 h-12 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Calculator className="h-4 w-4 mr-2" />
              Hiển thị kết quả
            </Button>
            {isNkudNote && (
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden w-72 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 shadow-lg group-hover:block z-10 text-center">
                Mã HS này chưa đủ điều kiện để tính thuế tự động (thuế NK ưu đãi có ghi chú đặc biệt).
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 border-b border-r border-amber-200 bg-amber-50" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Advanced Form */}
      {mode === 'advanced' && (
        <div className="space-y-5">
          {/* Exchange Rate - inline editable */}
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            {currency !== 'VND' ? (
              <>
                <span>Tỷ giá:</span>
                {isEditingRate ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">1 {currency} =</span>
                    <input
                      type="number"
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(Number(e.target.value) || 1)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Escape') setIsEditingRate(false)
                      }}
                      className="w-28 border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                    <span className="text-gray-500">VND</span>
                    <button
                      type="button"
                      onClick={() => setIsEditingRate(false)}
                      className="text-green-600 hover:text-green-700"
                      title="Xác nhận"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <strong>1 {currency} = {exchangeRate.toLocaleString('vi-VN')} VND</strong>
                    <button
                      type="button"
                      onClick={() => setIsEditingRate(true)}
                      className="text-gray-300 hover:text-[#1E40AF] transition-colors"
                      title="Chỉnh tỷ giá"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <span className="text-gray-400">Nhập khẩu bằng VND - không cần tỷ giá</span>
            )}
          </div>

          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TaxCalculatorInput
              label={`Số lượng (Đơn vị: ${hs.donviVN || 'kg/lít'}):`}
              value={quantity}
              onChange={setQuantity}
              helperText="Khối lượng hoặc thể tích hàng hóa nhập khẩu"
              showHelperText={showHelpText}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Điều khoản nhập khẩu (Incoterm 2010):
              </label>
              <Select
                value={incotermType}
                onValueChange={(value) => {
                  if (value === 'FOB' || value === 'CIF') {
                    setIncotermType(value)
                  }
                }}
              >
                <SelectTrigger className='w-full max-w-[260px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FOB">FOB - Free on Board</SelectItem>
                  <SelectItem value="CIF">CIF - Cost Insurance Freight</SelectItem>
                </SelectContent>
              </Select>
              {showHelpText && (
                <p className="mt-1.5 text-xs text-gray-500">Điều khoản thương mại quốc tế</p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Country Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nhập từ:</label>
              <CountrySelector
                value={country}
                onValueChange={setCountry}
              />
              {showHelpText && (
                <p className="mt-1.5 text-xs text-gray-500">Quốc gia xuất khẩu</p>
              )}
            </div>

            <TaxCalculatorInput
              label={`Đơn giá ${incotermType}`}
              value={unitPrice}
              onChange={setUnitPrice}
              helperText="Giá mỗi đơn vị"
              showHelperText={showHelpText}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Loại tiền</label>
              <Select value={currency} onValueChange={(value) => setCurrency(value as 'USD' | 'VND')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="VND">VND</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Thời gian nhập:</label>
              <Input
                type="date"
                value={importDate}
                onChange={(e) => setImportDate(e.target.value)}
                className="relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
              {showHelpText && (
                <p className="mt-1.5 text-xs text-gray-500">Ngày dự kiến nhập hàng</p>
              )}
            </div>

            {incotermType === 'FOB' && (
              <>
                <TaxCalculatorInput
                  label="Cước phí"
                  value={freight}
                  onChange={setFreight}
                  helperText="Chi phí vận chuyển"
                  showHelperText={showHelpText}
                />
                <TaxCalculatorInput
                  label="Bảo hiểm"
                  value={insurance}
                  onChange={setInsurance}
                  helperText="Phí bảo hiểm hàng hóa"
                  showHelperText={showHelpText}
                />
              </>
            )}
          </div>

          {/* TTDB */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Thuế TTĐB (Thuế tiêu thụ đặc biệt)</label>
            <div className={ttdbNote ? 'group relative' : undefined}>
              <Input
                type="text"
                value={ttdbRate !== undefined && ttdbRate >= 0 ? formatNumber(ttdbRate) : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[,.]/g, '')
                  if (value === '') {
                    setTtdbRate(undefined)
                  } else if (!isNaN(Number(value))) {
                    setTtdbRate(Number(value))
                  }
                }}
                placeholder={ttdbNote ?? "Bạn có thể (i) để trống, hoặc điền 0 nếu mặt hàng không bị áp thuế TTĐB, hoặc (ii) điền theo hướng dẫn, hoặc (iii) điền số bất kỳ"}
              />
              {ttdbNote && (
                <div className="pointer-events-none absolute bottom-full left-0 mb-2 hidden w-96 max-w-[90vw] rounded-lg border border-[#DBEAFE] bg-white p-3 text-xs text-[#404040] shadow-lg group-hover:block z-10">
                  <div className="mb-1 font-semibold text-[#1E40AF]">Ghi chú thuế TTĐB:</div>
                  <div className="whitespace-pre-wrap">{ttdbNote}</div>
                  <div className="absolute -bottom-1 left-4 h-2 w-2 rotate-45 border-b border-r border-[#DBEAFE] bg-white" />
                </div>
              )}
            </div>
            {showHelpText && (
              <p className="mt-1.5 text-xs text-gray-500">Thuế áp dụng cho một số mặt hàng đặc biệt như bia rượu, thuốc lá, ô tô...</p>
            )}
          </div>

          {/* VAT */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Thuế GTGT (VAT)</label>
            <div className={vatNote ? 'group relative' : undefined}>
              <Input
                type="text"
                value={vatRateInput !== undefined && vatRateInput >= 0 ? formatNumber(vatRateInput) : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[,.]/g, '')
                  if (value === '') {
                    setVatRateInput(undefined)
                  } else if (!isNaN(Number(value))) {
                    setVatRateInput(Number(value))
                  }
                }}
                placeholder={vatNote ?? `Mặc định ${vatRate}% – điền để ghi đè`}
              />
              {vatNote && (
                <div className="pointer-events-none absolute bottom-full left-0 mb-2 hidden w-96 max-w-[90vw] rounded-lg border border-[#DBEAFE] bg-white p-3 text-xs text-[#404040] shadow-lg group-hover:block z-10">
                  <div className="mb-1 font-semibold text-[#1E40AF]">Ghi chú thuế VAT:</div>
                  <div className="whitespace-pre-wrap">{vatNote}</div>
                  <div className="absolute -bottom-1 left-4 h-2 w-2 rotate-45 border-b border-r border-[#DBEAFE] bg-white" />
                </div>
              )}
            </div>
            {showHelpText && (
              <p className="mt-1.5 text-xs text-gray-500">Thuế giá trị gia tăng – thường 10%, một số hàng hóa áp dụng 5% hoặc 0%.</p>
            )}
          </div>

          {/* C/O Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Form C/O (Giấy chứng nhận xuất xứ)
            </label>
            <Select value={selectedFTA} onValueChange={setSelectedFTA}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Chọn Form C/O" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-co">
                  <span className="text-gray-500">Không có C/O</span>
                </SelectItem>
                <SelectItem value="no-co-fta">
                  <span className="text-gray-500">Không có C/O (thỏa mãn ưu đãi FTA)</span>
                </SelectItem>
                {availableFTAOptions.map((fta) => (
                  <SelectItem key={fta.key} value={fta.key}>
                    <span className="font-medium">{fta.form} - {fta.agreement}: {fta.taxRate}%</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showHelpText && (
              <p className="mt-1.5 text-xs text-gray-500">C/O để được hưởng ưu đãi thuế từ các hiệp định FTA</p>
            )}
          </div>

          {/* Service Checkbox */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasService}
                onChange={(e) => setHasService(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
              />
              <span className="text-sm font-medium text-gray-700">Có dịch vụ đi kèm?</span>
            </label>
            {showHelpText && (
              <p className="mt-1.5 text-xs text-gray-500">Tích chọn nếu có các dịch vụ khác kèm theo được thực hiện trong lãnh thổ Việt Nam như là: Vận chuyển, lắp đặt, bảo hành, bảo dưỡng, vận hành thử, …</p>
            )}

            {hasService && (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="serviceSeparated"
                      checked={serviceSeparated === 'no'}
                      onChange={() => setServiceSeparated('no')}
                      className="w-4 h-4 border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
                    />
                    <span className="text-sm text-gray-700">Không tách giá trị dịch vụ ra khỏi giá hàng hóa</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="serviceSeparated"
                      checked={serviceSeparated === 'yes'}
                      onChange={() => setServiceSeparated('yes')}
                      className="w-4 h-4 border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
                    />
                    <span className="text-sm text-gray-700">Tách giá trị dịch vụ ra khỏi giá hàng hóa </span>
                  </label>
                </div>

                {serviceSeparated === 'yes' && (
                  <div className="space-y-2">
                    {services.map((service, index) => (
                      <div key={index} className="flex gap-2 items-end">
                        <div className="flex-2">
                          {index === 0 && <label className="block text-xs text-gray-500 mb-1">Loại</label>}
                          <Select
                            value={service.type}
                            onValueChange={(value) => updateServiceType(index, value)}
                          >
                            <SelectTrigger className="w-full max-w-[500px]">
                              <SelectValue placeholder="Chọn loại dịch vụ" className="truncate" />
                            </SelectTrigger>
                            <SelectContent className="w-full max-w-[700px]">
                              <SelectItem value="-1">Chọn loại dịch vụ</SelectItem>
                              {Object.entries(SERVICE_TAX_RATES).map(([key, value]) => (
                                <SelectItem key={key} value={key} className="whitespace-normal">
                                  {value.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1 min-w-[120px]">
                          {index === 0 && <label className="block text-xs text-gray-500 mb-1">Giá trị</label>}
                          <div className="relative flex items-center">
                            <Input
                              type="number"
                              value={service.value}
                              onChange={(e) => updateServiceValue(index, Number(e.target.value))}
                              className="pr-14"
                            />
                            <span className="absolute right-3 text-xs text-gray-400 font-medium pointer-events-none select-none">
                              {currency}
                            </span>
                          </div>
                        </div>
                        {services.length > 1 && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => removeService(index)}
                            className="mb-1 cursor-pointer"
                          >
                            Xóa
                          </Button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={addService}
                      disabled={services.length >= 6}
                      className="text-sm text-[#1E40AF] cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      + Thêm dịch vụ
                    </button>
                    {services.length >= 6 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Đã đạt giới hạn tối đa 6 dịch vụ
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="group relative">
            <Button
              onClick={calculateTax}
              disabled={isNkudNote}
              className="w-full bg-[#1E40AF] hover:bg-[#1a3a9e] text-white py-3 h-12 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Calculator className="h-4 w-4 mr-2" />
              Hiển thị kết quả
            </Button>
            {isNkudNote && (
              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden w-72 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 shadow-lg group-hover:block z-10 text-center">
                Mã HS này chưa đủ điều kiện để tính thuế tự động (thuế NK ưu đãi có ghi chú đặc biệt).
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 border-b border-r border-amber-200 bg-amber-50" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Result Card */}
      <HsTaxCalculatorResultCard
        taxCalculationLines={taxCalculationLines}
        mode={mode}
        calculatedGoodsValue={calculatedGoodsValue}
        donviVN={hs.donviVN}
        totalExplanation={goodsValueFormula}
        onSwitchToAdvanced={() => { handleModeChange('advanced'); }}
      />
    </div>
  )
}
